import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { format, startOfWeek, addDays, subWeeks, addWeeks } from "date-fns";
import { Wallet, Download, ChevronLeft, ChevronRight } from "lucide-react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

const iso = (d) => format(d, "yyyy-MM-dd");

export default function Payroll() {
  const [weekStart, setWeekStart] = useState(() => startOfWeek(new Date(), { weekStartsOn: 1 }));
  const [data, setData] = useState({ rows: [], total_hours: 0 });
  const [loading, setLoading] = useState(false);
  const weekEnd = addDays(weekStart, 6);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/staff/payroll", { params: { start: iso(weekStart), end: iso(weekEnd) } });
      setData(data);
    } catch { toast.error("Failed to load payroll."); }
    finally { setLoading(false); }
  }, [weekStart]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { load(); }, [load]);

  function exportCsv() {
    if (!data.rows.length) return toast.error("No approved hours to export for this week.");
    const header = ["Worker", "Email", "Shifts", "Approved Hours"];
    const lines = data.rows.map((r) => [r.worker_name, r.worker_email, r.entries, r.hours.toFixed(2)]);
    lines.push(["TOTAL", "", data.rows.reduce((s, r) => s + r.entries, 0), data.total_hours.toFixed(2)]);
    const esc = (v) => `"${String(v).replace(/"/g, '""')}"`;
    const csv = [header, ...lines].map((row) => row.map(esc).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `payroll_${iso(weekStart)}_to_${iso(weekEnd)}.csv`;
    document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    toast.success("Payroll CSV downloaded.");
  }

  return (
    <div>
      <div className="flex items-center gap-3">
        <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-secondary text-primary"><Wallet className="h-6 w-6" /></div>
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight">Payroll</h1>
          <p className="text-sm text-muted-foreground">Weekly approved clock-in hours per worker. Only admin-approved shifts are counted.</p>
        </div>
      </div>

      <div className="mt-6 flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-border bg-card p-4">
        <div className="flex items-center gap-2">
          <Button variant="outline" size="icon" className="rounded-full" onClick={() => setWeekStart(subWeeks(weekStart, 1))} data-testid="payroll-prev-week"><ChevronLeft className="h-4 w-4" /></Button>
          <div className="min-w-[220px] text-center">
            <p className="font-heading font-bold" data-testid="payroll-week-range">{format(weekStart, "MMM d")} – {format(weekEnd, "MMM d, yyyy")}</p>
            <p className="text-xs text-muted-foreground">Mon–Sun</p>
          </div>
          <Button variant="outline" size="icon" className="rounded-full" onClick={() => setWeekStart(addWeeks(weekStart, 1))} data-testid="payroll-next-week"><ChevronRight className="h-4 w-4" /></Button>
          <Button variant="ghost" className="rounded-full" onClick={() => setWeekStart(startOfWeek(new Date(), { weekStartsOn: 1 }))} data-testid="payroll-this-week">This week</Button>
        </div>
        <Button className="rounded-full" onClick={exportCsv} data-testid="payroll-export"><Download className="mr-1 h-4 w-4" /> Export CSV</Button>
      </div>

      <div className="mt-6 overflow-hidden rounded-xl border border-border/60 bg-card">
        <Table>
          <TableHeader><TableRow><TableHead>Worker</TableHead><TableHead>Email</TableHead><TableHead className="text-right">Shifts</TableHead><TableHead className="text-right">Approved Hours</TableHead></TableRow></TableHeader>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={4} className="py-10 text-center text-muted-foreground">Loading…</TableCell></TableRow>
            ) : data.rows.length === 0 ? (
              <TableRow><TableCell colSpan={4} className="py-10 text-center text-muted-foreground" data-testid="payroll-empty">No approved hours for this week.</TableCell></TableRow>
            ) : (
              <>
                {data.rows.map((r, i) => (
                  <TableRow key={i} data-testid={`payroll-row-${i}`}>
                    <TableCell className="font-medium">{r.worker_name || "—"}</TableCell>
                    <TableCell className="text-sm text-muted-foreground">{r.worker_email}</TableCell>
                    <TableCell className="text-right">{r.entries}</TableCell>
                    <TableCell className="text-right font-semibold">{r.hours.toFixed(2)}</TableCell>
                  </TableRow>
                ))}
                <TableRow className="bg-secondary/40">
                  <TableCell className="font-heading font-bold" colSpan={2}>Total</TableCell>
                  <TableCell className="text-right font-bold">{data.rows.reduce((s, r) => s + r.entries, 0)}</TableCell>
                  <TableCell className="text-right font-bold" data-testid="payroll-total">{data.total_hours.toFixed(2)}</TableCell>
                </TableRow>
              </>
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
